import React, { useEffect, useRef } from 'react'

import { ModalRestrictor } from '@sketch/components'

import { NestedProjectItemGridView } from './NestedProjectItemGridView'
import { NestedProjectItemListView } from './NestedProjectItemListView'

import { StorageItemFragment } from '@sketch/gql-types'
import { RenderStorageItem } from '../../../storageItems/components'
import { noop } from 'lodash'

export interface NestedProjectItemProps {
  nestedProject: StorageItemFragment
  workspaceIdentifier: string
  onClick?: React.MouseEventHandler<HTMLElement>
  renderDropdown: RenderStorageItem<StorageItemFragment>
  disabledDrag: boolean
  presentation: 'grid' | 'list'
  selected?: boolean
  onSelectChange?: (selected: boolean) => void

  /* This property only applies to the grid presentation */
  showSelectionCheckbox?: boolean
}

export const NestedProjectItem: React.FCC<NestedProjectItemProps> = ({
  nestedProject,
  workspaceIdentifier,
  presentation,
  onClick,
  renderDropdown,
  disabledDrag,
  selected,
  onSelectChange,
  showSelectionCheckbox,
}) => {
  const handleItemDismiss = useRef(noop)
  handleItemDismiss.current = selected ? () => onSelectChange?.(false) : noop

  /**
   * If the item is being dismounted (removed) from the view we are preventively
   * unselecting it. This is to avoid having a selected item that is not visible
   * any-more in the UI.
   *
   * This can also occur when a project is moved or deleted and an event removes the
   * items from the listing
   */
  useEffect(() => {
    return () => {
      handleItemDismiss.current()
    }
  }, [])

  const Component =
    presentation === 'grid'
      ? NestedProjectItemGridView
      : NestedProjectItemListView

  return (
    <ModalRestrictor>
      <Component
        data-testid="nestedProject-item"
        nestedProject={nestedProject}
        workspaceIdentifier={workspaceIdentifier}
        renderDropdown={() => renderDropdown(nestedProject)}
        onClick={onClick}
        disabledDrag={disabledDrag}
        selected={selected}
        onSelectChange={onSelectChange}
        showSelectionCheckbox={showSelectionCheckbox}
      />
    </ModalRestrictor>
  )
}
